

















































import { mapGetters } from 'vuex';
import {
  cancelStreamAiStreamerConfig,
  deleteAiStreamerConfig,
  getAiStreamerConfigsList,
  stopAutoclickerJob,
} from '@/api/CopyStake/AutoClicker';
import {
  TAiStreamerFilter,
  TAiStreamerListItem
} from '@/views/CopyStake/CopyStakeAutoclicker/types';
import { IFilterData } from '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAiStreamersFilter.vue';
import { AxiosError } from 'axios';
import { errorToastMessage } from '@/helpers/errorToastMessage';

type TWebAiStreamersTabState = {
  showEditModal: boolean;
  showPlanStreamModal: boolean;
  showConfirmModal: boolean;
  aiStreamersList: TAiStreamerListItem[];
  selectedItem: TAiStreamerListItem;
  confirmModalPayload: any;
  loading: Record<string, boolean>;
  aiStreamersListFilters: IFilterData | null;
};

const CopyStakeAiStreamerEditModal = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAiStreamerEditModal.vue'
  );
const CopyStakeAiStreamerPlanModal = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAiStreamerPlanModal.vue'
  );
const CopyStakeAiStreamersFilter = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAiStreamersFilter.vue'
  );
const CopyStakeAiStreamersTable = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAiStreamersTable.vue'
  );
const ConfirmationDialog = (): Promise<any> =>
  import('@/components/ConfirmationDialog.vue');

export default {
  name: 'CopyStakeAiStreamersTab',
  components: {
    CopyStakeAiStreamerEditModal,
    CopyStakeAiStreamerPlanModal,
    CopyStakeAiStreamersFilter,
    CopyStakeAiStreamersTable,
    ConfirmationDialog
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data(): TWebAiStreamersTabState {
    return {
      showEditModal: false,
      showPlanStreamModal: false,
      showConfirmModal: false,
      aiStreamersList: [],
      selectedItem: undefined,
      confirmModalPayload: {},
      aiStreamersListFilters: null,
      loading: {
        list: false,
        deleteConfig: false,
        stopStream: false,
        cancelStream: false
      }
    };
  },
  watch: {
    active: {
      handler(active: boolean): void {
        if (active) {
          this.getAiStreamersList();
        }
      },
      immediate: false
    },
    operatorId: {
      handler(): void {
        if (this.operatorId !== null) {
          this.getAiStreamersList();
        }
      },
      immediate: true
    },
    showEditModal(val: boolean): void {
      if (!val) {
        this.resetSelectedItem();
      }
    },
    showPlanStreamModal(val: boolean): void {
      if (!val) {
        this.resetSelectedItem();
      }
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    }
  },
  methods: {
    openEditModal(): void {
      this.showEditModal = true;
    },
    openPlanStreamModal(): void {
      this.showPlanStreamModal = true;
    },

    async getAiStreamersList(): Promise<void> {
      this.loading.list = true;

      const filters = this.aiStreamersListFilters
        ? ({
            [this.aiStreamersListFilters.searchBy]:
              this.aiStreamersListFilters.searchPhrase
          } as TAiStreamerFilter)
        : null;

      getAiStreamerConfigsList(filters)
        .then((data) => {
          this.aiStreamersList = data;
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);
          this.aiStreamersList = [];
        })
        .finally(() => {
          this.loading.list = false;
        });
    },
    deleteConfig(item: TAiStreamerListItem): void {
      this.loading.deleteConfig = true;

      deleteAiStreamerConfig(item.id)
        .then(() => {
          this.$toast.success(
            `AI Streamer for "${item.nickname}" was successfully deleted`
          );

          this.getAiStreamersList();
          this.showConfirmModal = false;
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.deleteConfig = false;
        });
    },
    stopStream(item: TAiStreamerListItem): void {
      this.loading.stopStream = true;

      stopAutoclickerJob(item.activeJobId, false)
        .then(() => {
          this.$toast.success(`Stream was successfully stopped`);

          this.getAiStreamersList();
          this.showConfirmModal = false;
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.stopStream = false;
        });
    },
    cancelStream(item: TAiStreamerListItem): void {
      this.loading.cancelStream = true;

      cancelStreamAiStreamerConfig(item)
        .then(() => {
          this.$toast.success(`Stream was successfully canceled`);

          this.getAiStreamersList();
          this.showConfirmModal = false;
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.cancelStream = false;
        });
    },
    resetSelectedItem(): void {
      this.selectedItem = undefined;
    },
    handleUpdateFilter(filters: IFilterData | null): void {
      this.aiStreamersListFilters = filters || null;

      this.getAiStreamersList();
    },
    handleEditAiStreamerDialog(item: TAiStreamerListItem): void {
      this.selectedItem = { ...item };

      this.openEditModal();
    },
    handleCopyAiStreamerDialog(item: TAiStreamerListItem): void {
      this.selectedItem = {
        ...item,
        startTime: '',
        endTime: '',
        id: null
      };

      this.openEditModal();
    },
    handlePlanAiStreamerDialog(item: TAiStreamerListItem): void {
      this.selectedItem = { ...item };

      this.openPlanStreamModal();
    },
    handleDeleteAiStreamer(item: TAiStreamerListItem): void {
      this.confirmModalPayload = {
        header: 'Delete AI Streamer Config',
        okText: 'Delete',
        handler: () => this.deleteConfig(item),
        icon: 'mdi-alert-octagon',
        disclaimer: `
          <p>
            Are you sure you want to delete AI Streamer config for nickname <strong>${item.nickname}</strong>?
          </p>
          <p class="text--disabled">You will not be able to recover it.</p>`
      };
      this.showConfirmModal = true;
    },
    handleStopStreamDialog(item: TAiStreamerListItem): void {
      this.confirmModalPayload = {
        header: 'Stop AI Streamer?',
        okText: 'Stop Stream',
        handler: () => this.stopStream(item),
        disclaimer: `
          <p>
            Are you sure you want to stop the stream for <strong>${item.nickname}</strong>? This will end the current session.
          </p>`
      };
      this.showConfirmModal = true;
    },
    handleCancelStreamDialog(item: TAiStreamerListItem): void {
      this.confirmModalPayload = {
        header: 'Cancel AI Streamer?',
        okText: 'Yes, Cancel',
        handler: () => this.cancelStream(item),
        disclaimer: `
          <p>
          Are you sure you want to cancel the <strong>${item.nickname}</strong> stream?
          </p>`,
        okButtonType: 'warning'
      };
      this.showConfirmModal = true;
    }
  }
};
